import { FormBuilderState } from "components/containers/inspectionSetting/containers/templateBuilder/context/FormBuilderContext/types";
import { ActionMap } from "interfaces/models/context";
import { uniqBy, cloneDeep } from "lodash-es";
import { Ctx } from "../containers/selections/setup";
import {
  Item,
  Partial,
} from "components/containers/InspectionFormat/containers/LayoutFrameBuilder/type";

export const DEFAULT_EXTRA_ID = "default";
export const LINKED_DATA_KEY_NO = "no";

interface CommonLayout {}

export type CommonLayoutFrame = {
  items: Item[];
  itemSelectedId: string | null;
  itemSelected: Item | null;
  partials: Record<string, Record<string, Record<string, Partial>>>;
  CommonLayout: CommonLayout;
  cloneCommonLayoutId?: string;
  src: string;
  sourceData: {
    formBuilderState?: FormBuilderState | null;
    categories?: string[];
  };
  ctx: Ctx;
  zoom: number;
  clonePageLayoutId: null | string;
};

export enum SOURCE {
  BIM = "bim",
  ASSET = "asset",
}

// Define the initial state
export const initialCommonLayoutFrame: CommonLayoutFrame = {
  items: [],
  partials: {},
  itemSelectedId: null,
  itemSelected: null,
  CommonLayout: null as any,
  src: null as any,
  sourceData: {},
  ctx: {},
  zoom: 1,
  clonePageLayoutId: null,
};

export enum Types {
  ADD_ITEM = "ADD_ITEM",
  ADD_ITEMS = "ADD_ITEMS",
  DELETE_ITEM = "DELETE_ITEM",
  UPDATE_ITEM = "UPDATE_ITEM",
  SET_PARTIAL = "SET_PARTIAL",
  SET_PARTIALS = "SET_PARTIALS",
  DELETE_PARTIAL = "DELETE_PARTIAL",
  SET_ITEM_SELECTED = "SET_ITEM_SELECTED",
  RESET_ITEMS = "RESET_ITEMS",
}

type CommonLayoutFramePayload = {
  [Types.ADD_ITEM]: Item;
  [Types.ADD_ITEMS]: Item[];
  [Types.RESET_ITEMS]: Item[];
  [Types.UPDATE_ITEM]: Item;
  [Types.DELETE_ITEM]: string;
  [Types.SET_ITEM_SELECTED]: string | null;
  [Types.SET_PARTIAL]: Partial;
  [Types.SET_PARTIALS]: Partial[];
  [Types.DELETE_PARTIAL]: { key: string };
};

export type CommonLayoutFrameActions =
  ActionMap<CommonLayoutFramePayload>[keyof ActionMap<CommonLayoutFramePayload>];

// Define the reducer function
const CommonLayoutFrameReducer = (
  prevState: CommonLayoutFrame,
  action: CommonLayoutFrameActions
) => {
  const nextState = cloneDeep(prevState);

  switch (action.type) {
    case Types.RESET_ITEMS:
      nextState.items = action.payload;
      nextState.items = uniqBy(nextState.items, "id");
      break;
    case Types.ADD_ITEMS:
      nextState.items.push(...action.payload);
      nextState.items = uniqBy(nextState.items, "id");
      break;
    case Types.ADD_ITEM:
      nextState.items.push(action.payload);
      nextState.itemSelectedId = action.payload.id;
      break;
    case Types.UPDATE_ITEM:
      {
        const index = nextState.items.findIndex(
          (item) => item.id === action.payload.id
        );
        if (index !== -1) {
          nextState.items[index] = {
            ...nextState.items[index],
            ...action.payload,
          };
        }
      }
      break;
    case Types.DELETE_ITEM:
      const index = nextState.items.findIndex(
        (item) => item.id === action.payload
      );
      if (index !== -1) {
        nextState.items.splice(index, 1);
      }
      if (action.payload === nextState.itemSelectedId) {
        nextState.itemSelectedId = null;
      }
      break;
    case Types.SET_PARTIAL:
      {
        const { id, source, extraId } = action.payload;
        nextState.partials[source] ??= {};
        if (!nextState.partials[source][id])
          nextState.partials[source][id] = {};
        nextState.partials[source][id][extraId ?? DEFAULT_EXTRA_ID] =
          action.payload;
      }
      break;
    case Types.SET_PARTIALS:
      action.payload.forEach((data) => {
        const { id, source, extraId } = data;
        nextState.partials[source] ??= {};
        if (!nextState.partials[source][id])
          nextState.partials[source][id] = {};
        nextState.partials[source][id][extraId ?? DEFAULT_EXTRA_ID] = data;
      });
      break;
    case Types.DELETE_PARTIAL:
      {
        const { key } = action.payload;
        delete nextState.partials[key];
      }
      break;
    case Types.SET_ITEM_SELECTED:
      nextState.itemSelectedId = action.payload;
      break;
  }

  return nextState;
};

export { CommonLayoutFrameReducer };
